import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, Box, CircularProgress, ThemeProvider } from '@mui/material';
import CaseStatusLayout from './CaseStatusLayout';
import { CaseStatus } from '../../logic/components';
import {
  CaseStatusApi,
  CaseStatusTokenError,
} from '../../logic/components/CaseStatus/api/CaseStatusApi';
import { CaseStatusData } from '../../logic/components/CaseStatus/types/CaseStatusData';
import { ContactInfo } from '../../logic/components/CaseStatus/types/ContactInfo';
import { PaymentProvider } from '../../logic/components/CaseStatus/types/PaymentTypes';
import { useAuthenticationContext } from '../../core/services/AuthenticationProvider';
import caseStatusTheme from '../../logic/components/CaseStatus/caseStatusTheme';
import GlobalConfig from '../../core/api/GlobalConfig';
import CaseStatusLanguageProvider, { useCaseStatusLanguage } from './CaseStatusLanguageProvider';

const CaseStatusContent = () => {
  const location = useLocation<{ accessToken?: string }>();
  const history = useHistory();
  const caseNumber = new URLSearchParams(location.search).get('caseNumber') ?? '';
  const accessToken = location.state?.accessToken ?? '';
  const authContext = useAuthenticationContext();
  const { t, translationsReady, enableTranslations } = useCaseStatusLanguage();

  const [data, setData] = useState<CaseStatusData | null>(null);
  const [contact, setContact] = useState<ContactInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    if (!caseNumber) {
      setIsLoading(false);
      setNotFound(true);
      return;
    }

    if (!accessToken) {
      history.replace(`/case-verify?caseNumber=${encodeURIComponent(caseNumber)}`);
      return;
    }

    setIsLoading(true);
    setNotFound(false);

    Promise.all([
      CaseStatusApi.getCaseStatus(caseNumber, accessToken),
      CaseStatusApi.getContactInfo(accessToken),
    ])
      .then(([result, contactResult]) => {
        if (result) {
          setData(result);
          setContact(contactResult);
        } else {
          setNotFound(true);
        }
        enableTranslations();
      })
      .catch((error) => {
        if (error instanceof CaseStatusTokenError) {
          history.replace(`/case-verify?caseNumber=${encodeURIComponent(caseNumber)}`);
        } else {
          setNotFound(true);
        }
      })
      .finally(() => setIsLoading(false));
  }, [caseNumber]);

  const handleLoginClick = () => {
    authContext.signinRedirect?.();
  };

  const handleTokenExpired = () => {
    history.replace(`/case-verify?caseNumber=${encodeURIComponent(caseNumber)}`);
  };

  const payWithoutLoginEnabled = GlobalConfig.val().MyPagePayWithoutLoginEnabled ?? false;

  const showLoading = isLoading || !translationsReady;

  const handlePickProvider = async () => {
    setProvidersLoading(true);
    try {
      const result = await CaseStatusApi.getPaymentProviders(accessToken);
      setProviders(result);
    } catch {
      handleTokenExpired();
    } finally {
      setProvidersLoading(false);
    }
  };

  const handleConfirmProvider = async (provider: PaymentProvider) => {
    setConfirmLoading(true);
    try {
      const redirect = await CaseStatusApi.initiatePayment(caseNumber, provider.type, accessToken);
      if (redirect.paymentToken) {
        sessionStorage.setItem(`paymentToken:${caseNumber}`, redirect.paymentToken);
      }
      if (redirect.method === 'GET') {
        window.location.href = redirect.url;
      } else {
        const range = document.createRange();
        const fragment = range.createContextualFragment(redirect.body ?? '');
        document.body.appendChild(fragment);
        document.querySelector<HTMLButtonElement>('#submit-button')?.click();
      }
    } catch (error) {
      setConfirmLoading(false);
      if (error instanceof CaseStatusTokenError) {
        handleTokenExpired();
      } else {
        window.location.href = `/case-payment-result?caseNumber=${encodeURIComponent(caseNumber)}`;
      }
    }
  };

  return (
    <ThemeProvider theme={caseStatusTheme}>
      <CaseStatusLayout>
        {showLoading && (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 8 }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}

        {!showLoading && notFound && (
          <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4, px: 2 }}>
            <Alert severity="error">
              {t('CaseNotFoundBody', caseNumber)}
            </Alert>
          </Box>
        )}

        {!showLoading && data && (
          <CaseStatus
            data={data}
            contact={contact}
            payWithoutLoginEnabled={payWithoutLoginEnabled}
            providers={providers}
            providersLoading={providersLoading}
            confirmLoading={confirmLoading}
            onLoginClick={handleLoginClick}
            onPickProvider={handlePickProvider}
            onConfirmProvider={handleConfirmProvider}
          />
        )}
      </CaseStatusLayout>
    </ThemeProvider>
  );
};

const CaseStatusRoute = () => {
  const location = useLocation<{ accessToken?: string }>();
  const history = useHistory();
  const accessToken = location.state?.accessToken ?? '';
  const caseNumber = new URLSearchParams(location.search).get('caseNumber') ?? '';

  const loadTranslations = useCallback(
    (culture: string) =>
      accessToken ? CaseStatusApi.getTranslations(culture, accessToken) : Promise.resolve({}),
    [accessToken]
  );

  const handleTokenExpired = useCallback(() => {
    history.replace(`/case-verify?caseNumber=${encodeURIComponent(caseNumber)}`);
  }, [history, caseNumber]);

  return (
    <CaseStatusLanguageProvider
      deferLoad
      loadTranslations={loadTranslations}
      onTokenExpired={handleTokenExpired}
    >
      <CaseStatusContent />
    </CaseStatusLanguageProvider>
  );
};

export default CaseStatusRoute;
